/**
 * Catalogue de machines de l'entreprise (décision du 12 octobre 2026).
 *
 * Une fiche du catalogue décrit un type de machine que l'entreprise installe ou
 * entretient souvent : identité (catégorie, marque, modèle, fluide), entretien
 * conseillé, prix de vente et de pose, pièces habituelles. Elle appartient à
 * l'entreprise ; aucune marque ni aucun prix commun n'est installé. Une machine
 * créée depuis le catalogue COPIE ces valeurs et garde la référence de la fiche
 * et sa version : modifier le catalogue ne réécrit jamais une machine, un devis
 * ou un contrat déjà enregistré.
 */
import { z } from 'zod';

export const MACHINE_MODEL_MAX_PER_COMPANY = 500;
export const MACHINE_MODEL_MAX_PARTS = 50;
/** Même plafond que les lignes des modèles tarifaires (`pricingLine`). */
export const MACHINE_MODEL_MAX_PRICE_CENTS = 100_000_000;
/** Une tonne de fluide : au-delà, c'est une faute de frappe. */
export const MACHINE_MODEL_MAX_CHARGE_GRAMS = 1_000_000;

/** Texte facultatif : vide après nettoyage devient `null`. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .default(null)
    .transform((value) => (value ? value : null));

export const machineModelPart = z
  .object({
    articleId: z.guid(),
    /** En millièmes, comme les quantités de stock (1 000 = une unité). */
    quantityMilli: z.number().int().min(1).max(1_000_000),
  })
  .strict();

export const machineModelBody = z
  .object({
    name: z.string().trim().min(1).max(200),
    category: z.string().trim().min(1).max(100),
    brand: optionalText(100),
    model: optionalText(100),
    fluidType: optionalText(40),
    fluidChargeGrams: z.number().int().min(0).max(MACHINE_MODEL_MAX_CHARGE_GRAMS).nullable().default(null),
    photoFileId: z.guid().nullable().default(null),
    notes: optionalText(4000),
    visitsPerYear: z.number().int().min(1).max(52).nullable().default(null),
    visitDurationMinutes: z.number().int().min(5).max(1440).nullable().default(null),
    maintenanceTemplateId: z.guid().nullable().default(null),
    salePriceCents: z.number().int().min(0).max(MACHINE_MODEL_MAX_PRICE_CENTS).nullable().default(null),
    installPriceCents: z
      .number()
      .int()
      .min(0)
      .max(MACHINE_MODEL_MAX_PRICE_CENTS)
      .nullable()
      .default(null),
    vatBp: z.number().int().min(0).max(10000).nullable().default(null),
    parts: z
      .array(machineModelPart)
      .max(MACHINE_MODEL_MAX_PARTS)
      .default([])
      .refine(
        (parts) => new Set(parts.map((part) => part.articleId)).size === parts.length,
        'Chaque article ne figure qu’une fois parmi les pièces habituelles.',
      ),
    active: z.boolean().default(true),
  })
  .strict()
  .refine(
    (value) =>
      (value.salePriceCents === null && value.installPriceCents === null) || value.vatBp !== null,
    { error: 'Indiquez le taux de TVA des prix de vente et de pose.', path: ['vatBp'] },
  );

export type MachineModelInput = z.input<typeof machineModelBody>;
export type MachineModelBody = z.output<typeof machineModelBody>;

export interface MachineModelPart {
  articleId: string;
  quantityMilli: number;
  reference: string;
  label: string;
  unit: string;
  /** Article retiré ou archivé depuis : la pièce reste listée, signalée. */
  available: boolean;
}

/** Fiche telle que l'API la sert. Les prix sont `null` sans le droit de les voir. */
export interface MachineModel extends Omit<MachineModelBody, 'parts'> {
  id: string;
  version: number;
  parts: MachineModelPart[];
  /** Machines créées depuis cette fiche (toutes versions confondues). */
  equipmentCount: number;
  maintenanceTemplateName: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Résumé servi sur la fiche d'une machine créée depuis le catalogue. */
export interface EquipmentMachineModel {
  model: MachineModel;
  /** Version de la fiche copiée à la création de la machine. */
  copiedVersion: number;
}

/** Une visite tous les N mois pour N visites par an (au moins un mois, au plus douze). */
export function monthsBetweenVisits(visitsPerYear: number): number {
  return Math.min(12, Math.max(1, Math.round(12 / visitsPerYear)));
}
